import type { Rule } from '../../core/assetType.js';
import type { ValidationIssue } from '../../core/issues.js';

import { isNonEmptyString, isRecord, shapeIssue } from './_helpers/shapeIssues.js';

const err = shapeIssue('checklist-ending integrity');

interface NodeLike {
  id?: unknown;
  nodeType?: unknown;
  exitRoutes?: unknown;
  options?: unknown;
  narratorDialogue?: unknown;
  characterDialogue?: unknown;
}

interface InnerLike {
  nodes?: unknown;
}

/**
 * content.checklistEnding — an `ending:checklist` node is terminal and renders its
 * `options` as-is: an optional `hero` (title required when present), a `content`
 * block (title, description) and a non-empty `items` list of strings. The player
 * mounts nothing for a checklist ending without items, so that is an error, not a
 * warning. Dialogue authored on the node never plays (the recap shows on entry).
 *
 * Inert for content with no checklist endings.
 */
export const checklistEndingShape: Rule<unknown> = {
  code: 'content.checklistEnding',
  check: (input): ValidationIssue[] => {
    const data = input as InnerLike;
    const nodes: NodeLike[] = Array.isArray(data.nodes) ? (data.nodes as NodeLike[]) : [];
    const issues: ValidationIssue[] = [];

    nodes.forEach((node, i) => {
      if (node.nodeType !== 'ending:checklist') {
        return;
      }
      const id = node.id;

      if (node.exitRoutes !== undefined && node.exitRoutes !== null) {
        issues.push(err(
          'content.checklistEnding.exitRoutes',
          `Checklist ending '${id ?? '?'}' has exitRoutes; ending nodes must be terminal`,
          ['nodes', i, 'exitRoutes'],
          id,
          node.exitRoutes,
        ));
      }

      for (const dead of ['narratorDialogue', 'characterDialogue'] as const) {
        if (Array.isArray(node[dead]) && (node[dead] as unknown[]).length > 0) {
          issues.push(err(
            'content.checklistEnding.ignored',
            `Checklist ending '${id ?? '?'}': '${dead}' never plays on ending:checklist nodes: the recap shows the moment the node is entered`,
            ['nodes', i, dead],
            id,
            node[dead],
            'warning',
          ));
        }
      }

      const base: (string | number)[] = ['nodes', i, 'options'];
      const opts = node.options;
      if (!isRecord(opts)) {
        issues.push(err(
          'content.checklistEnding.options',
          `Checklist ending '${id ?? '?'}' must have an 'options' object (hero, content, items)`,
          base,
          id,
          opts,
        ));
        return;
      }

      if (opts.hero !== undefined) {
        if (!isRecord(opts.hero)) {
          issues.push(err(
            'content.checklistEnding.hero',
            `Checklist ending '${id ?? '?'}': options.hero must be an object (title, breadcrumb, backgroundGradient, illustration)`,
            [...base, 'hero'],
            id,
            opts.hero,
          ));
        }
        else if (!isNonEmptyString(opts.hero.title)) {
          issues.push(err(
            'content.checklistEnding.hero',
            `Checklist ending '${id ?? '?'}': options.hero.title must be a non-empty string`,
            [...base, 'hero', 'title'],
            id,
            opts.hero.title,
          ));
        }
      }

      if (!isRecord(opts.content)) {
        issues.push(err(
          'content.checklistEnding.content',
          `Checklist ending '${id ?? '?'}': options.content must be an object (title, description)`,
          [...base, 'content'],
          id,
          opts.content,
        ));
      }
      else {
        for (const k of ['title', 'description'] as const) {
          if (!isNonEmptyString(opts.content[k])) {
            issues.push(err(
              'content.checklistEnding.content',
              `Checklist ending '${id ?? '?'}': options.content.${k} must be a non-empty string`,
              [...base, 'content', k],
              id,
              opts.content[k],
            ));
          }
        }
      }

      const items = opts.items;
      if (!Array.isArray(items) || items.length === 0) {
        issues.push(err(
          'content.checklistEnding.items',
          `Checklist ending '${id ?? '?'}': options.items must be a non-empty array of strings: the player renders nothing for a checklist with no items`,
          [...base, 'items'],
          id,
          items,
        ));
        return;
      }
      items.forEach((item, ii) => {
        if (!isNonEmptyString(item)) {
          issues.push(err(
            'content.checklistEnding.items',
            `Checklist ending '${id ?? '?'}': options.items[${ii}] must be a non-empty string`,
            [...base, 'items', ii],
            id,
            item,
          ));
        }
      });
    });

    return issues;
  },
};
